import { FLAVOUR_INFO, type Menu, type MenuItem } from './menu.js';
import type { LineConfig, Portion } from './pricing.js';
import { formatPence } from './money.js';

/*
 * Plain-English descriptions of a configured line, shared by the app (bag,
 * order screens), the API (order snapshots, receipts) and later the kitchen
 * ticket. Same wording everywhere: flavours, then sauce, removals, extras.
 */

const NBSP = ' ';

export function joinAnd(parts: readonly string[]): string {
  if (parts.length < 2) return parts.join('');
  return parts.slice(0, -1).join(', ') + ' and ' + parts[parts.length - 1];
}

/** "1× Angara chicken + 1× Malai chicken" */
export function portionPhrase(portions: readonly Portion[]): string {
  if (!portions.length) return '';
  const groups: { key: string; flavour: string; protein: string; n: number }[] = [];
  for (const p of portions) {
    const key = `${p.flavour}|${p.protein}`;
    let g = groups.find((x) => x.key === key);
    if (!g) {
      g = { key, flavour: p.flavour, protein: p.protein, n: 0 };
      groups.push(g);
    }
    g.n++;
  }
  return groups.map((g) => `${g.n}×${NBSP}${g.flavour}${NBSP}${g.protein}`).join(`${NBSP}+ `);
}

/** A line in one short phrase for a card (Order again, saved orders): a tikka item's flavours, worded as the bag's first line, else the item's name. */
export function lineSummary(menu: Menu, cfg: LineConfig): string {
  const it = menu.items[cfg.itemId];
  if (it?.hasTikkas && cfg.portions.length) return portionPhrase(cfg.portions);
  return it?.name ?? cfg.itemId;
}

/** Sauce, then removals, then extras. `building` shows "pick a sauce" before one is chosen. */
export function modsOf(menu: Menu, cfg: LineConfig, building = false): string[] {
  const it = menu.items[cfg.itemId];
  const m: string[] = [];
  if (!it) return m;
  if (it.hasTikkas) m.push(cfg.sauces.length ? joinAnd(cfg.sauces) + ' sauce' : building ? 'pick a sauce' : 'no sauce');
  if (it.hasToppings) for (const t of cfg.toppingsOff) m.push('no ' + t.toLowerCase().replace('iceberg ', ''));
  for (const u of cfg.upgradeIds) {
    const up = menu.upgrades[u];
    if (up) m.push('with ' + up.name);
  }
  if (cfg.meal) m.push('meal with ' + cfg.meal.side + (cfg.meal.drink ? ' and ' + cfg.meal.drink : ''));
  return m;
}

export function modsLine(menu: Menu, cfg: LineConfig, building = false): string {
  const m = modsOf(menu, cfg, building).join(', ');
  return m ? m.charAt(0).toUpperCase() + m.slice(1) : '';
}

/** The till's changes in sentence case: "No onion, extra mint". */
export function changesLine(changes: readonly string[]): string {
  const m = changes.map((c) => c.toLowerCase()).join(', ');
  return m ? m.charAt(0).toUpperCase() + m.slice(1) : '';
}

/** "Rice Bowl · Large" */
export function titleOf(menu: Menu, cfg: LineConfig, sizeIndex = cfg.sizeIndex): string {
  const it = menu.items[cfg.itemId];
  if (!it) return cfg.itemId;
  const size = it.sizes[sizeIndex];
  return it.sizes.length > 1 && size ? `${it.name} · ${size.name}` : it.name;
}

/** "Large Rice Bowl": the size then the format, with no made-up name (Saif, 16 Oct 2026). */
export function sizeTitle(menu: Menu, cfg: LineConfig, sizeIndex = cfg.sizeIndex): string {
  const it = menu.items[cfg.itemId];
  if (!it) return cfg.itemId;
  const size = it.sizes[sizeIndex];
  return it.sizes.length > 1 && size ? `${size.name} ${it.name}` : it.name;
}

/** "The Fire & Cream Bowl" from the flavours in the bowl. Kept for marketing copy; the app no longer shows it. */
export function bowlName(item: MenuItem | undefined, portions: readonly Portion[]): string {
  const nicks = [...new Set(portions.map((p) => p.flavour))].map((f) => FLAVOUR_INFO[f]?.nick ?? f);
  if (!nicks.length) return '';
  const noun = item?.noun ?? 'Bowl';
  if (nicks.length === 1) return `The ${nicks[0]} ${noun}`;
  return `${nicks.slice(0, -1).join(', ')} & ${nicks[nicks.length - 1]} ${noun}`;
}

/** The reward tag shown after the description. */
export function rewardLabel(cfg: LineConfig, sizeIndex: number): string {
  const r = cfg.reward;
  if (!r) return '';
  if (r.kind === 'points') return r.rewardId === 'main' && sizeIndex > 0 ? `Free main, ${r.points.toLocaleString('en-GB')} points + Large upgrade` : `Free for ${r.points.toLocaleString('en-GB')} points`;
  if (r.kind === 'code') return 'Free with your code';
  return 'Free spin prize';
}

/** One-line description: "1× Angara chicken + 1× Malai chicken · Mint and Garlic sauce, no onion · Free for 1,000 points" */
export function describeLine(menu: Menu, cfg: LineConfig, sizeIndex = cfg.sizeIndex): string {
  const it = menu.items[cfg.itemId];
  const parts: string[] = [];
  if (cfg.membersPrice && it) return `Members' price · usually ${formatPence(it.sizes[0]?.pricePence ?? 0)}`;
  if (it?.hasTikkas && cfg.portions.length) parts.push(portionPhrase(cfg.portions));
  // A till line lists its changes only (house sauces are on top unless a change says otherwise).
  const ml = cfg.changes !== undefined ? changesLine(cfg.changes) : modsLine(menu, cfg);
  if (ml) parts.push(ml);
  const rl = rewardLabel(cfg, sizeIndex);
  if (rl) parts.push(rl);
  return parts.join(' · ');
}

/** Two-line description for the bag: flavours on line one, the rest on line two. */
export function describeLines(menu: Menu, cfg: LineConfig, sizeIndex = cfg.sizeIndex): string[] {
  const it = menu.items[cfg.itemId];
  const out: string[] = [];
  if (cfg.membersPrice && it) return [`Members' price · usually ${formatPence(it.sizes[0]?.pricePence ?? 0)}`];
  if (it?.hasTikkas && cfg.portions.length) out.push(portionPhrase(cfg.portions));
  const ml = cfg.changes !== undefined ? changesLine(cfg.changes) : modsLine(menu, cfg);
  if (ml) out.push(ml);
  const rl = rewardLabel(cfg, sizeIndex);
  if (rl) out.push(rl);
  return out;
}
